import React, { useState, useEffect } from 'react';
import { FORM_MODE, Permissions, STATE_STATUS } from '@awp/constants';
import { Row, Col, Button, FormControl } from 'react-bootstrap';
import Authorize from '@awp/components/Authorize';
import AwpYesNoDialog from '@awp/components/Common/AwpYesNoDialog';
import { ActionMenuView, ActionMenuEdit, ActionMenuDelete } from '@awp/components/Common/AwpActionMenus';
import Table from '@components/table';
import { sortByNumericThenAlpha } from '@awp/helpers/sorter';
import { Crud } from '@awp/shared/Crud';

const SoilTypesTable = ({ isMedium, isLarge, setMode, mode, setData, soilTypes, soilTypesLoading, onDelete }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredSoilTypes, setFilteredSoilTypes] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const crud = new Crud('Soil Types', mode);

  useEffect(() => {
    if (soilTypes) {
      const filtered = soilTypes.filter(
        (item) =>
          item.Code.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.Description.toLowerCase().includes(searchTerm.toLowerCase()),
      );
      setFilteredSoilTypes(filtered.sort((a, b) => a.Code.localeCompare(b.Code)));
    }
  }, [soilTypes, searchTerm]);

  const handleDeleteConfirm = () => {
    if (selectedRow && onDelete) {
      onDelete(selectedRow);
    }
    setShowDeleteConfirm(false);
    setSelectedRow(null);
  };

  const actionMenu = (row) => (
    <div>
      <Authorize requires={[Permissions.WriteSoilType]}>
        <ActionMenuEdit
          onClick={() => {
            setData(row.row.original);
            setMode(FORM_MODE.EDIT);
          }}
        />
        <ActionMenuDelete
          onClick={() => {
            setSelectedRow(row.row.original);
            setShowDeleteConfirm(true);
          }}
        />
      </Authorize>
      <ActionMenuView
        onClick={() => {
          setData(row.row.original);
          setMode(FORM_MODE.VIEW);
        }}
      />
    </div>
  );

  const columns = React.useMemo(
    () => [
      { Header: 'Soil Type Code', accessor: 'Code', style: { width: '150px' }, sortType: sortByNumericThenAlpha },
      { Header: 'Description', accessor: 'Description', style: { width: '300px' }, sortType: sortByNumericThenAlpha },
      { Header: 'Actions', accessor: 'Id', style: { width: '150px' }, disableSortBy: true, Cell: actionMenu },
    ],
    [isLarge, isMedium],
  );

  return (
    <>
      <div className="d-flex mb-3">{crud.getHeader()}</div>
      <Row className="mb-3">
        <Col sm={4}>
          <FormControl
            type="text"
            placeholder="Search by Code or Description"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Col>
      </Row>
      {soilTypesLoading === STATE_STATUS.LOADING ? (
        <div>Loading...</div>
      ) : filteredSoilTypes.length > 0 ? (
        <Table
          columns={columns}
          data={filteredSoilTypes}
          fetchData={() => {}}
          search={searchTerm}
          defaultSortBy="Code"
        />
      ) : (
        <div className="mt-4">
          <p>No records found.</p>
        </div>
      )}
      <div style={{ paddingTop: '1.25rem' }}>
        {!showDeleteConfirm && (
          <Authorize requires={[Permissions.WriteSoilType]}>
            <Button className="mr-2" variant="primary" onClick={() => setMode(FORM_MODE.CREATE)}>
              Create
            </Button>
          </Authorize>
        )}
        {showDeleteConfirm && selectedRow && (
          <AwpYesNoDialog
            msg={`Are you sure you want to delete soil type "${selectedRow.Code}"?`}
            setShowYesNoConfirm={setShowDeleteConfirm}
            action={() => handleDeleteConfirm()}
            props={{}}
          />
        )}
      </div>
    </>
  );
};

export default SoilTypesTable;
